import { difference, union } from 'lodash';
import { fromPairs, groupBy, indexBy, pipe } from 'ramda';
import { ProjectId } from 'api/models';
import { FieldValueType, Task as TaskFromApi } from 'api/tasks';
import type { AssigneeValue } from 'api/tasks/assignee-value';
import {
  Content,
  CreatableEditableRefreshableContent,
  Deleting,
  Empty,
  Loaded,
  Loading,
  RefreshableContent,
  Refreshing,
  Updating,
} from 'stores/core';
import { TaskLookupItem } from 'utils/to-store-task';

export type Task = Omit<TaskFromApi, 'project' | 'fields' | 'assignee'> & {
  parentTask?: number;
  project: number;
  assignee?: AssigneeValue[];
  taskTree: Pick<Task, 'taskName' | 'taskId' | 'parentTask'>[];
} & {
  [fieldName: string]: FieldValueType;
};

interface Store<T> {
  byId: Record<number, CreatableEditableRefreshableContent<T>>;
  byProjectId: Record<number, RefreshableContent<number[]>>;
  projectIds: number[];
  ids: Content<number[]>;
  creating: {
    byId: Record<string, number>;
    ids: string[];
  };
}

export type TasksStore = Store<Task>;

const EMPTY: TasksStore = {
  byId: {},
  byProjectId: {},
  ids: Empty(),
  projectIds: [],
  creating: {
    byId: {},
    ids: [],
  },
};

export type TaskCreationRequest = Omit<Task, 'taskId'>;

const _creating =
  (creating_task: TaskCreationRequest, id: string) =>
  ({
    byId,
    byProjectId: {
      [creating_task.project as number]: tasksInProject = Empty(),
      ...byProjectId
    },
    projectIds,
    ids,
    creating: { byId: creatingById, ids: creatingIds },
  }: TasksStore): TasksStore => {
    const [, taskIds = []] = ids;
    const [, taskIdsInProject = []] = tasksInProject;
    const task_without_tree = {
      ...creating_task,
      taskId: -1 - creatingIds.length,
    } as unknown as Task;
    const task = {
      ...task_without_tree,
      taskTree: [TaskLookupItem(task_without_tree)],
    } as unknown as Task;

    return {
      byId: { ...byId, [task.taskId]: Loaded(task) },
      byProjectId: {
        ...byProjectId,
        [task.project]: Loaded(union(taskIdsInProject, [task.taskId])),
      },
      projectIds,
      ids: Loaded(union(taskIds, [task.taskId])),
      creating: {
        byId: { ...creatingById, [id]: task.taskId },
        ids: union(creatingIds, [id]),
      },
    };
  };

const added =
  (task: Task) =>
  ({
    byId,
    byProjectId: { [task.project]: tasksInProject = Empty(), ...byProjectId },
    projectIds,
    ids,
    creating,
  }: TasksStore): TasksStore => {
    const [, taskIds = []] = ids;
    const [, taskIdsInProject = []] = tasksInProject;

    return {
      byId: { ...byId, [task.taskId]: Loaded(task) },
      byProjectId: {
        ...byProjectId,
        [task.project]: Loaded(union(taskIdsInProject, [task.taskId])),
      },
      projectIds,
      ids: Loaded(union(taskIds, [task.taskId])),
      creating,
    };
  };

const created =
  (task: Task, id: string) =>
  (store: TasksStore): TasksStore => {
    if (!store.creating.ids.includes(id)) {
      return added(task)(store);
    }
    const {
      byId,
      byProjectId: { [task.project]: tasksInProject = Empty(), ...byProjectId },
      projectIds,
      ids,
      creating: { byId: creatingById, ids: creatingIds },
    } = store;
    const [, taskIds = []] = ids;
    const [, taskIdsInProject = []] = tasksInProject;
    const taskIdToRemove = creatingById[id];
    const { [taskIdToRemove]: creatingId, ...otherIds } = byId;
    const { [id]: creatingReference, ...otherCreatingReferences } =
      creatingById;

    return {
      byId: { ...otherIds, [task.taskId]: Loaded(task) },
      byProjectId: {
        ...byProjectId,
        [task.project]: Loaded(
          difference(union(taskIdsInProject, [task.taskId]), [taskIdToRemove]),
        ),
      },
      projectIds,
      ids: Loaded(difference(union(taskIds, [task.taskId]), [taskIdToRemove])),
      creating: {
        byId: otherCreatingReferences,
        ids: difference(creatingIds, [id]),
      },
    };
  };

const loadingProjectTasks =
  ([, projectId]: ProjectId) =>
  ({
    byId,
    byProjectId,
    projectIds,
    ids,
    creating,
  }: TasksStore): TasksStore => {
    const project = byProjectId[projectId] ?? Empty();
    const next =
      project[0] === 'refreshing' || project[0] === 'loading'
        ? project
        : project[0] === 'loaded'
        ? Refreshing(project)
        : Loading();

    return {
      byId,
      byProjectId: { ...byProjectId, [projectId]: next },
      projectIds: union(projectIds, [projectId]),
      ids,
      creating,
    };
  };

const loadedAllTasks =
  (tasks: Task[]) =>
  ({ projectIds, creating }: TasksStore): TasksStore => {
    const tasksByProjectId = pipe(
      () => tasks,
      groupBy((x) => x.project.toString()),
    )();
    const byProjectId = fromPairs(
      projectIds.map((p) => [
        p,
        Loaded((tasksByProjectId[p] ?? []).map((t) => t.taskId)),
      ]),
    );
    const byId = pipe(
      () => tasks.map(Loaded),
      indexBy((x) => x[1].taskId),
    )();
    const ids = Loaded(tasks.map((x) => x.taskId));

    return {
      creating,
      projectIds,
      byProjectId,
      byId,
      ids,
    };
  };

const loadedProjectTasks =
  ([, projectId]: ProjectId, tasks: Task[]) =>
  ({
    byId,
    byProjectId,
    projectIds,
    ids,
    creating,
  }: TasksStore): TasksStore => {
    const [, taskIds = []] = ids;

    return {
      byId: tasks.reduce(
        (prev, task) => ({ ...prev, [task.taskId]: Loaded(task) }),
        byId,
      ),
      byProjectId: {
        ...byProjectId,
        [projectId]: Loaded(tasks.map((task) => task.taskId)),
      },
      projectIds: union(projectIds, [projectId]),
      ids: Loaded(
        union(
          taskIds,
          tasks.map((x) => x.taskId),
        ),
      ),
      creating,
    };
  };

const _updating =
  (task: Task) =>
  (store: TasksStore): TasksStore => {
    const { byId, byProjectId, projectIds, ids, creating } = store;
    const {
      [task.taskId]: [, content],
    } = byId;

    if (content == null) {
      return added(task)(store);
    }

    return {
      byId: { ...byId, [task.taskId]: Updating(task, content!) },
      byProjectId,
      projectIds,
      ids,
      creating,
    };
  };

const updating =
  (tasks: Task[]) =>
  (store: TasksStore): TasksStore =>
    tasks.reduce((state, task) => _updating(task)(state), store);

const _updated =
  (task: Task) =>
  (store: TasksStore): TasksStore => {
    const { byId, byProjectId, projectIds, ids, creating } = store;

    const content = byId?.[task.taskId]?.[1];

    if (content == null) {
      return added(task)(store);
    }

    return {
      byId: { ...byId, [task.taskId]: Loaded(task) },
      byProjectId,
      projectIds,
      ids,
      creating,
    };
  };

const updated =
  (tasks: Task[]) =>
  (store: TasksStore): TasksStore =>
    tasks.reduce((state, task) => _updated(task)(state), store);

const _revert =
  (taskId: number) =>
  (store: TasksStore): TasksStore => {
    const { byId, byProjectId, projectIds, ids, creating } = store;
    const { [taskId]: task } = byId;

    if (task[0] !== 'updating') {
      return store;
    }

    return {
      byId: { ...byId, [taskId]: Loaded(task[2]) },
      byProjectId,
      projectIds,
      ids,
      creating,
    };
  };

const revert =
  (taskIds: number[]) =>
  (store: TasksStore): TasksStore =>
    taskIds.reduce((state, taskId) => _revert(taskId)(state), store);

const _deleting =
  (task: Task) =>
  (store: TasksStore): TasksStore => {
    const { byId, byProjectId, projectIds, ids, creating } = store;
    const {
      [task.taskId]: [, content],
    } = byId;

    if (content == null) {
      return store;
    }

    return {
      byId: { ...byId, [task.taskId]: Deleting(task) },
      byProjectId,
      projectIds,
      ids,
      creating,
    };
  };

const deleting =
  (tasks: Task[]) =>
  (store: TasksStore): TasksStore =>
    tasks.reduce((state, task) => _deleting(task)(state), store);

const _deleted =
  (taskId: Task['taskId']) =>
  (store: TasksStore): TasksStore => {
    const { byId, byProjectId, projectIds, ids, creating } = store;

    if (!(ids[1] ?? []).includes(taskId)) {
      return store;
    }

    const {
      [taskId]: [, task],
      ...rest
    } = byId;
    const [, taskIds = []] = ids;

    if (task == null) {
      return store;
    }

    const lookup = byProjectId[task.project as number] ?? Empty();

    return {
      byId: rest,
      byProjectId:
        lookup[0] === 'empty' ||
        lookup[0] === 'loading' ||
        lookup[0] === 'refreshing'
          ? byProjectId
          : {
              ...byProjectId,
              [task.project]: Loaded(
                lookup[1].filter((x) => x !== task.taskId),
              ),
            },
      projectIds,
      ids: Loaded(taskIds.filter((x) => x !== taskId)),
      creating,
    };
  };

const deleted =
  (taskIds: number[]) =>
  (store: TasksStore): TasksStore =>
    taskIds.reduce((state, taskId) => _deleted(taskId)(state), store);

const _restored =
  (taskId: Task['taskId']) =>
  (store: TasksStore): TasksStore => {
    const { byId, byProjectId, projectIds, ids, creating } = store;
    const { [taskId]: task } = byId;

    if (task == null || task[0] !== 'deleting') {
      return store;
    }

    return {
      byId: { ...byId, [taskId]: Loaded(task[1]) },
      byProjectId,
      projectIds,
      ids,
      creating,
    };
  };

const restored =
  (taskIds: number[]) =>
  (store: TasksStore): TasksStore =>
    taskIds.reduce((state, taskId) => _restored(taskId)(state), store);

const failed =
  (id: string) =>
  (store: TasksStore): TasksStore => {
    const {
      creating: { byId, ids },
    } = store;
    const taskId = store.creating.byId[id];

    if (taskId == null) {
      return store;
    }
    const { [id]: failure, ...rest } = byId;

    return deleted([taskId])({
      ...store,
      creating: {
        byId: rest,
        ids: difference(ids, [id]),
      },
    });
  };

const ofProjectId =
  ([, projectId]: ProjectId) =>
  ({ byProjectId, byId }: TasksStore) =>
    RefreshableContent.map((taskIds: number[]) =>
      taskIds
        .map((t) => byId[t])
        .filter(Boolean)
        .map(([, x]) => x!)
        .filter(Boolean),
    )(byProjectId[projectId] ?? Empty());

export const TasksStore = {
  empty: () => EMPTY,
  creating: _creating,
  created,
  failed,
  add: added,
  loadingProjectTasks,
  loadedProjectTasks,
  loadedAllTasks,
  updating,
  updated,
  revert,
  deleting,
  deleted,
  restored,
  ofProjectId,
};
